/**
 * Lightweight event bus for the onboarding tour to drive transient UI state
 * (force-open sidebars, open mobile sheets) without prop-threading through the
 * whole layout. Layout regions (CanvasArea, MobileLayout) subscribe with
 * `subscribeOnboardingUI`; the tour dispatches via the helpers below.
 *
 * Snapshot/restore is done by the tour:
 *   1. On tour start: dispatch 'snapshot' — listeners record their current state.
 *   2. During tour: dispatch 'force-region' to open sidebars, etc.
 *   3. On tour end: dispatch 'restore' — listeners revert to the snapshot.
 */

export type OnboardingRegion =
  | 'sidebar-left'
  | 'sidebar-right'
  | 'mobile-tools'
  | 'mobile-properties'
  | 'none';

export interface OnboardingUIEventMap {
  'fw-onboarding-ui': {
    type: 'snapshot' | 'restore' | 'force-region' | 'release-region';
    region?: OnboardingRegion;
  };
}

const EVENT = 'fw-onboarding-ui';

export function dispatchOnboardingUI(
  detail: OnboardingUIEventMap['fw-onboarding-ui'],
) {
  try {
    window.dispatchEvent(new CustomEvent(EVENT, { detail }));
  } catch {
    /* no-op (SSR/test) */
  }
}

export function subscribeOnboardingUI(
  handler: (detail: OnboardingUIEventMap['fw-onboarding-ui']) => void,
) {
  const listener = (e: Event) => {
    const ce = e as CustomEvent<OnboardingUIEventMap['fw-onboarding-ui']>;
    if (ce.detail) handler(ce.detail);
  };
  window.addEventListener(EVENT, listener);
  return () => window.removeEventListener(EVENT, listener);
}

/**
 * Interaction events the tour listens for to auto-advance. Workflow components
 * dispatch these; GuideOverlay matches against the current step's `advanceOn`.
 *
 * Event names use `<subject>:<verb>` convention:
 *   - block:click, block:dblclick, block:moved, block:rightclick
 *   - tool:dropped
 *   - connector:created
 *   - layer:entered
 *   - group:created
 *   - seed-demo (dispatched by tour, handled by page shell to pre-populate)
 */
export interface OnboardingInteractionEvent {
  name: string;
  /** Optional target identifier (e.g. `block`, `connector-path`, a blockId). */
  target?: string;
  /** Free-form metadata for custom events. */
  data?: Record<string, unknown>;
}

const INTERACTION_EVENT = 'fw-onboarding-event';

export function dispatchOnboardingEvent(event: OnboardingInteractionEvent) {
  try {
    window.dispatchEvent(new CustomEvent(INTERACTION_EVENT, { detail: event }));
  } catch {
    /* no-op */
  }
}

export function subscribeOnboardingEvent(
  handler: (event: OnboardingInteractionEvent) => void,
) {
  const listener = (e: Event) => {
    const ce = e as CustomEvent<OnboardingInteractionEvent>;
    if (ce.detail) handler(ce.detail);
  };
  window.addEventListener(INTERACTION_EVENT, listener);
  return () => window.removeEventListener(INTERACTION_EVENT, listener);
}

/**
 * Maps a `data-guide` target to the region that must be open for the target
 * to be visible. Used by GuideOverlay to auto-expand the right panel before
 * measuring.
 */
export function regionForTarget(target: string | null | undefined, isMobile: boolean): OnboardingRegion {
  if (!target) return 'none';
  // Right-side properties panel
  if (target === 'properties' || target.startsWith('properties-')) {
    return isMobile ? 'mobile-properties' : 'sidebar-right';
  }
  // Left-side tool sidebar
  if (
    target === 'tool-sidebar' ||
    target === 'tool-sidebar-items' ||
    target === 'layer-tree'
  ) {
    return isMobile ? 'mobile-tools' : 'sidebar-left';
  }
  // Minimap lives inside right sidebar (PropertiesPanel)
  if (target === 'minimap') {
    return isMobile ? 'mobile-properties' : 'sidebar-right';
  }
  return 'none';
}
